// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CheckoutHeaderPhone from "@guestsupply/App/Components/Checkout/CheckoutHeaderPhone";
import xHeaderUpdate from "@guestsupply/App/Handlers/XNG/Header";
import useSize, { useElementSize } from "@guestsupply/App/Hooks/useSize";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import {
    getCartLinkWidget,
    getGlobalBannerMessageWidget,
    getHeaderActionsWidget,
    getHeaderLanguageWidget,
    getHeaderLinkListWidget,
    getHeaderOrderGuideWidget,
    getHeaderSearchWidget,
    getHeaderSignInWidget,
    getLogoImageWidget,
    getMainNavigationWidget,
    getSecondaryNavigationWidget,
} from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { checkoutPages, GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import Close from "@guestsupply/XDL/Icons/Close";
import Menu from "@guestsupply/XDL/Icons/Menu";
import { color, layoutContainer, xClassNames, xScreen } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        logoImageWidget: getLogoImageWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerSignInWidget: getHeaderSignInWidget(state),
        cartLinkWidget: getCartLinkWidget(state),
        headerActionsWidget: getHeaderActionsWidget(state),
        secondaryNavigationWidget: getSecondaryNavigationWidget(state),
        headerLinkListWidget: getHeaderLinkListWidget(state),
        headerOrderGuideWidget: getHeaderOrderGuideWidget(state),
        headerSearchWidget: getHeaderSearchWidget(state),
        headerLanguageWidget: getHeaderLanguageWidget(state),
        isTabletScreen: state.xCommon.isTabletScreen,
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        globalBannerMessageWidget: getGlobalBannerMessageWidget(state),
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
        currentBillTo: getCurrentBillToState(state).value,
        currentPage: getCurrentPage(state).type,
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    navigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    // XNG-CHANGES
    wrapperStyledWrapper?: InjectableCss;
    logoLinksSearchWrapper?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    itemsWrapper?: InjectableCss;
    headerLinkListWrapper?: InjectableCss;
    mainNavigationPageWrapper?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    closeMenuClickable?: ClickableProps;
    navigationAndLinkedListWrapper?: InjectableCss;
    wrapper?: InjectableCss;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            top: 0;
            left: 0;
            z-index: ${getProp("theme.zIndex.compactHeader")};
            box-shadow: 0 1px 0 0 ${color("common.border")};
            opacity: 0;
            transform: translateY(-100%) translateX(0);
            padding: ${props => props.theme.header.padding};
            background-color: ${getColor("common.white")};
            &&&& {
                position: fixed;
            }
            ${xScreen("lte", "md")} {
                display: none;
            }
            ${generateBorder}
            ${generateShadow};
        `,
    },
    logoWrapper: {
        css: css`
            display: flex;
            > *:first-child {
                margin-right: 12px;
            }
            > *:nth-child(2) {
                text-align: right;
            }
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
            flex-grow: 1;
            max-width: 520px;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    navigationWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            position: absolute;
            background: ${color("common.white")};
            width: 100%;
            top: -200px;
            transition: top 500ms ease-in-out;
            margin-top: 1px;
            box-shadow: 0 1px 0 0 ${color("common.border")};
            ${({ showMainNavigation }: any) =>
                showMainNavigation &&
                css`
                    top: 100%;
                `}
            > div {
                ${layoutContainer}
            }
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        mainNavigation: {
            menuItemTypography: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        logo: {
            wrapper: {
                css: css`
                    width: 224px;
                    max-height: 42px;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                `,
            },
        },
        headerSearchInput: {
            searchInputStyles: {
                popoverContentBody: {
                    as: "div",
                    _height: "auto",
                    css: css`
                        display: inline-flex;
                        justify-content: flex-end;
                        border-radius: 3px;
                    `,
                },
                searchHistoryStyles: {
                    wrapper: {
                        css: css`
                            display: flex;
                            flex-direction: column;
                            width: 240px;
                            padding: 0 20px 15px 20px;
                        `,
                    },
                },
            },
        },
    } as Styles);

const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

const InnerWrapper = styled.div<{ isEdit?: boolean; isCheckoutPage?: boolean } & InjectableCss>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    ${(props: any) => {
        if (props.isEdit) {
            return "padding: 30px 10px;";
        }

        return props.isCheckoutPage ? "padding: 12px 16px;" : "padding: 15px;";
    }}
    width: 100%;
    justify-content: space-between;
    margin: 0 auto;
    position: relative;
    background-color: ${getColor("common.white")};
    z-index: inherit;
    ${({ theme, isEdit }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return isEdit
            ? css`
                  max-width: 100%;
              `
            : breakpointMediaQueries(
                  theme,
                  maxWidths.map((mw: any) =>
                      mw
                          ? css`
                                max-width: ${mw}px;
                            `
                          : null,
                  ),
              );
    }}
    & .logo-and-search-wrapper {
        display: flex;
        flex-grow: 1;
        gap: 54px;
    }
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    // <XNG-Changes>
    const [showMainNavigation, setShowMainNavigation] = useState(false);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const { isTabletScreen, stickyHeader } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        stickyHeader: (state as CustomAppState).xHeader.stickyHeader,
    }));
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useElementSize("compact-header")?.height;
    // </XNG-Changes>

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            // XNG-Changes
            // const windowWidth = Math.round(window.innerWidth);
            let isSticky = false;
            if (scrollPos > Number(props.theme.header.triggerPoint)) {
                setIsShow(true);
                isSticky = true;
            } else if (isShow && scrollPos < Number(props.theme.header.triggerPoint) + 150) {
                setIsShow(false);
                isSticky = false;
            }

            props.xHeaderUpdate({ key: "isSticky", value: isSticky });
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    // <XNG-Changes>
    useEffect(() => {
        if (headerHeight) {
            props.xHeaderUpdate({ key: "stickyHeader", value: `${headerHeight}` });
        }
    }, [headerHeight]);

    const openNavigationDrawer = () => {
        setShowMainNavigation(prevState => !prevState);
    };

    if (isTabletScreen) {
        return null;
    }
    // </XNG-Changes>

    // xng-change: add google event
    const getDataLayerEvent = (title: string) => {
        const dataLayer = getDataLayer();
        const { xGoogleAnalytics, currentPage, currentBillTo } = props;

        if (xGoogleAnalytics.includeToolBarNavigationEvent) {
            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.TOOL_BAR_NAVIGATION,
                page_path: currentPage,
                click_text: title,
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            } as any;

            dataLayer.push(googleAnalyticsData);
        }
    };

    // XNG-Changes: structure
    return (
        <Transition in={isShow} timeout={0}>
            {state => (
                <Page
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    // XNG-Changes
                    id="compact-header"
                    data-test-selector="compact-header"
                    className={xClassNames.append(isShow ? "shown-compact-header" : "hidden-compact-header")}
                    style={{
                        transition: "all 350ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        // XNG-Changes
                        maxWidth: headerWidth,
                    }}
                    itemRef=""
                >
                    <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                        {createWidgetElement("GlobalBannerMessage/GlobalBannerMessage", {
                            fields: {
                                ...props.globalBannerMessageWidget,
                            },
                        })}
                        <StyledWrapper {...styles.wrapper}>
                            <InnerWrapper
                                {...styles.pageInnerWrapper}
                                isEdit={isEdit}
                                isCheckoutPage={props.isCheckoutPage}
                            >
                                <StyledWrapper className="logo-and-search-wrapper" {...styles.logoLinksSearchWrapper}>
                                    <StyledWrapper
                                        {...styles.logoWrapper}
                                        // xng-change: add google event
                                        onClick={() => getDataLayerEvent("Logo")}
                                    >
                                        {!props.isCheckoutPage && (
                                            <Clickable
                                                onClick={openNavigationDrawer}
                                                data-test-selector="expandMobileMenu"
                                                aria-label="main-navigation"
                                                {...styles.closeMenuClickable}
                                            >
                                                <Icon
                                                    src={showMainNavigation ? Close : Menu}
                                                    size={showMainNavigation ? 20 : 34}
                                                    color="secondary"
                                                />
                                            </Clickable>
                                        )}
                                        {/* review image color */}
                                        {createWidgetElement("Basic/Logo", {
                                            fields: {
                                                mobileSpecificImage: props.logoImageWidget?.logoImage,
                                                logoImage: props.logoImageWidget?.logoImage,
                                            },
                                        })}
                                    </StyledWrapper>

                                    {!props.isCheckoutPage && (
                                        <StyledWrapper
                                            {...styles.searchWrapper}
                                            // xng-change: add google event
                                            onClick={() => getDataLayerEvent("HeaderSearchInput")}
                                        >
                                            {createWidgetElement("Header/HeaderSearchInput", {
                                                fields: {
                                                    ...props.headerSearchWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                {!props.isCheckoutPage && (
                                    <StyledWrapper {...styles.rightSectionWrapper}>
                                        <StyledWrapper
                                            {...styles.itemsWrapper}
                                            // xng-change: add google event
                                            onClick={() => getDataLayerEvent("OrderGuide")}
                                        >
                                            {createWidgetElement("Header/OrderGuide", {
                                                fields: {
                                                    ...props.headerOrderGuideWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.itemsWrapper}
                                            // xng-change: add google event
                                            onClick={() => getDataLayerEvent("HeaderSignIn")}
                                        >
                                            {createWidgetElement("Common/HeaderSignIn", {
                                                fields: {
                                                    ...props.headerSignInWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.itemsWrapper}
                                            // xng-change: add google event
                                            onClick={() => getDataLayerEvent("CartLink")}
                                        >
                                            {createWidgetElement("Common/CartLink", {
                                                fields: {
                                                    ...props.cartLinkWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.itemsWrapper}
                                            // xng-change: add google event
                                            onClick={() => getDataLayerEvent("Language")}
                                        >
                                            {createWidgetElement("Header/Language", {
                                                fields: {
                                                    ...props.headerLanguageWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                <CheckoutHeaderPhone />
                            </InnerWrapper>
                        </StyledWrapper>
                        {!props.isCheckoutPage ? (
                            <StyledWrapper {...{ showMainNavigation }} {...styles.navigationWrapper}>
                                <Page {...styles.mainNavigationPageWrapper}>
                                    <StyledWrapper {...styles.navigationAndLinkedListWrapper}>
                                        <StyledWrapper
                                            {...styles.mainNavigationWrapper}
                                            css={css`
                                                padding: 0 !important;
                                            `}
                                        >
                                            {createWidgetElement("Header/MainNavigation", {
                                                fields: { ...props.mainNavigationWidget },
                                            })}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.headerLinkListWrapper}>
                                            {createWidgetElement("Header/HeaderLinkList", {
                                                fields: { ...props.headerLinkListWidget },
                                            })}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </Page>
                            </StyledWrapper>
                        ) : (
                            <></>
                        )}
                    </StylesProvider>
                </Page>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
